import type { ChatMessage } from "@/stores/chat.store";

export type ChatMessageActionMode = "standard" | "committed" | "hidden";

export interface ChatMessageActionPresentation {
  mode: ChatMessageActionMode;
  traceId: string | null | undefined;
  isTurnStreaming: boolean;
}

interface TurnActionState {
  hasCommittedFoods: boolean;
  lastCommittedMessageId?: string;
  traceId?: string | null;
}

const turnKeyForMessage = (message: ChatMessage): string => {
  if (message.clientTurnId) return `client:${message.clientTurnId}`;
  if (message.turnId) return `server:${message.turnId}`;
  if (message.planFoodsCommitted) return `basket:${message.planFoodsCommitted.basketId}`;
  return `message:${message.id}`;
};

/**
 * Assigns one action owner per committed-food turn. The terminal trace can
 * live on assistant copy after the deterministic card, so ownership and trace
 * lookup both operate at turn level instead of relying on list position.
 */
export const resolveChatMessageActions = (
  messages: ChatMessage[],
  isStreaming: boolean,
): Map<string, ChatMessageActionPresentation> => {
  const turns = new Map<string, TurnActionState>();

  messages.forEach((message) => {
    if (message.role !== "agent") return;
    const turnKey = turnKeyForMessage(message);
    const state = turns.get(turnKey) ?? { hasCommittedFoods: false };

    if (message.planFoodsCommitted) {
      state.hasCommittedFoods = true;
      state.lastCommittedMessageId = message.id;
    }
    if (message.traceId !== undefined) state.traceId = message.traceId;
    turns.set(turnKey, state);
  });

  const activeTurnKey =
    isStreaming && messages.length > 0 ? turnKeyForMessage(messages.at(-1)!) : null;
  const presentations = new Map<string, ChatMessageActionPresentation>();

  messages.forEach((message) => {
    const turnKey = turnKeyForMessage(message);
    const state = turns.get(turnKey);
    const isTurnStreaming = turnKey === activeTurnKey;

    if (!state?.hasCommittedFoods) {
      presentations.set(message.id, {
        mode: "standard",
        traceId: message.traceId,
        isTurnStreaming,
      });
      return;
    }

    const ownsCommittedActions = message.id === state.lastCommittedMessageId;
    presentations.set(message.id, {
      // The action row belongs to the deterministic card and should arrive
      // with it. Feedback stays disabled until the terminal trace appears.
      mode: ownsCommittedActions ? "committed" : "hidden",
      traceId: ownsCommittedActions ? state.traceId : message.traceId,
      isTurnStreaming,
    });
  });

  return presentations;
};
